/**
 * Echo Hunt -- the corpus index prompt (P5, §4.1 + §4.2 + §4.3 + §4.4).
 *
 * VISIBILITY-MATRIX ROW (§16.0): "Index + self-contained rewrite".
 *  - knows real/fake?      NO relevance -- this is a real-only step, cached per bullet.
 *  - sees world/character?  NO.
 *  - sees other bullets?    its OWN SOURCE DREAM only (so references like "she said again" resolve).
 *  - sees corpus?           NO.
 *
 * One call per source DREAM (not per bullet), for exactly that reason: a bullet that says "the same
 * room" or "she reversed on it" is only resolvable against its sibling bullets.
 *
 * The prompt is deliberately built from `SourceBullet`s rather than DB rows, so it is PURE and can be
 * unit-tested against the real fixture post (see `Index.test.ts`).
 *
 * NOTE (v1, decided 2026-10-20): there is NO lucidity/lucid-phase field. The user's call: judging which
 * moment of a dream was lucid is not reliable enough (they often cannot tell either), and nothing
 * downstream consumed it. `lucidityMetaCognition` as a FILTER is unrelated and still applies -- the
 * story's character believes they are awake, so bullets about realizing/RCing/stabilizing are dropped.
 */
import {JSONSchemaLike} from "../LLM.js";
import {SourceBullet} from "../Bullets.js";
import {
	BulletEventType_values,
	BulletFilterReason_modelValues,
} from "../Types.js";

/** The house-style rules the rewrite must obey (§4.3). Kept here (rather than in `Normalize.ts`)
 * because the model needs them stated, while `Normalize.ts` only enforces the deterministic subset. */
export const HOUSE_STYLE_RULES = [
	"No `--` dashes; use commas or full stops.",
	"No `word1/word2` slash forms; pick one word or write \"X or Y\".",
	"No `()` or `[]` parentheticals; fold the content into the sentence or drop it.",
	"No recall hedges (\"I think it was\", \"not sure about\", \"hard to describe\", \"I vaguely recall\"). State the event plainly.",
	"Write in the first person, past tense, as a remembered event (not as speculation about a dream).",
	"Do not comment on recall quality, dream-ness or the journal itself.",
].map(a=>`- ${a}`).join("\n");

/** What each `eventType` means. Short on purpose: the enum is a coarse tag for supply-matching
 * (§5.3), not a taxonomy. */
export const EVENT_TYPE_DESCRIPTIONS: Record<string, string> = {
	powerUse: "the dreamer does something impossible for a waking person (flying, summoning, stopping time, changing the environment by will)",
	perception: "an unusual sight/sound/touch/visual effect is noticed, or the dreamer examines a strange sensory detail",
	social: "a conversation or interaction with another person/being is the core of the event",
	movement: "travelling, walking, driving, arriving somewhere -- the core is the change of place",
	sceneChange: "the setting itself changes (a shift to a new location/scene) rather than the dreamer acting",
	interaction: "the dreamer manipulates an object or the environment, or an object behaves impossibly",
	other: "none of the above fits, or several fit equally",
};

/** What each filter reason means (§4.4). Only the reasons the MODEL may return are listed; the
 * deterministic post-pass owns `styleViolation` and `indexFailed`. */
export const FILTER_REASON_DESCRIPTIONS: Record<string, string> = {
	thoughtOnly: "the bullet is purely internal (a thought, feeling or plan) with no event happening",
	hedgeDominated: "the bullet is mostly hedging/about recall rather than describing the event",
	metaCommentary: "the bullet is a note to self, a later interpretation, or journal bookkeeping",
	wakingComparison: "the bullet's core is a comparison to waking life (\"like my real house, but...\")",
	lucidityMetaCognition: "the bullet is about realizing it's a dream, doing reality checks, or stabilizing -- the story's character believes they are awake, so this cannot be used",
	bannedTerm: "the bullet depends on a term the user banned (or an equivalent unambiguous reference to it)",
	tooShort: "there is not enough material to make a standalone event out of it",
	noEvent: "after removing context, no event remains",
	empty: "there is nothing usable at all",
};

export function Index_SystemPrompt(): string {
	return [
		"You index bullet points from a private dream journal.",
		"Reply with JSON only. No prose, no code fences, no explanation.",
	].join("\n");
}

export type Index_UserPromptOptions = {
	dreamId: string;
	bullets: SourceBullet[];
	/** terms that must not appear in the rewritten text (§4.4 bannedTerm) */
	bannedWords?: string[]|n;
	/** cliché phrases the rewrite must avoid (§5.5) */
	blacklist?: string[]|n;
	/** on the second attempt: the problems found by the deterministic post-pass in attempt 1 */
	fixNotes?: string|n;
};

export function Index_UserPrompt(opts: Index_UserPromptOptions): string {
	const {dreamId, bullets, bannedWords, blacklist, fixNotes} = opts;

	const bulletLines = bullets.map((bullet, ordinal)=>{
		const heading = bullet.heading != null ? ` [section: ${bullet.heading}]` : "";
		return `${ordinal}.${heading} ${bullet.rawText}`;
	}).join("\n");

	const parts = [
		`Dream: ${dreamId}`,
		`Bullets: ${bullets.length}`,
		"",
		"## Task",
		"For EVERY bullet above, produce one record:",
		"",
		"1. `selfContainedText` -- rewrite the bullet as a STANDALONE event.",
		"   - Resolve every reference to an earlier bullet in this same dream (\"she said again\", \"the same room\", \"it\", \"that\") so the bullet makes sense on its own.",
		"   - Keep the event itself: the same actions, objects, places and people. Do not add drama, symbolism or interpretation. Do not summarise it into a theme.",
		"   - Keep roughly the same length as the source bullet (a short bullet stays short; do not pad it to a paragraph).",
		"   - It is normal for this journal to contain impossible events (flight, powers, places that change shape). Describe them literally and matter-of-factly; never remark that they are impossible or dreamlike.",
		"",
		"2. `eventType` -- one of:",
		...BulletEventType_values.map(value=>`   - ${value}: ${EVENT_TYPE_DESCRIPTIONS[value]}`),
		"",
		"3. `oddityCount` -- how many concrete objects, beings or places in the CORE event would be out of place in ordinary waking life (an object that does not belong there, a being that does not exist, a place that could not exist). Count them; 0 when the event is ordinary. Cap at 4.",
		"",
		"4. `filterReason` -- `null` when the bullet is usable; otherwise the SINGLE best reason:",
		...BulletFilterReason_modelValues.map(value=>`   - ${value}: ${FILTER_REASON_DESCRIPTIONS[value]??value}`),
		"   Still fill in `selfContainedText` for a filtered bullet (use the bullet's own text if nothing can be salvaged).",
		"",
		"## House style (applies to `selfContainedText`)",
		HOUSE_STYLE_RULES,
	];
	if ((bannedWords ?? []).length > 0) {
		parts.push(
			"",
			"## Banned terms",
			"The user excluded these. Do not use them, any inflection of them, or an obvious stand-in for one. If a bullet's meaning DEPENDS on one, set `filterReason` to `bannedTerm`; otherwise write around it.",
			...(bannedWords ?? []).map(a=>`- ${a}`),
		);
	}
	if ((blacklist ?? []).length > 0) {
		parts.push(
			"",
			"## Phrases to avoid (clichés)",
			"These read as generic machine-written dream prose. Do not use them, and do not write their equivalents.",
			...(blacklist ?? []).map(a=>`- ${a}`),
		);
	}

	parts.push(
		"",
		"## The bullets",
		bulletLines,
		"",
		"## Output",
		`Reply with ONE JSON object: {"bullets": [ {"ordinal": 0, "selfContainedText": "...", "eventType": "...", "oddityCount": 0, "filterReason": null}, ... ]}`,
		`Return exactly ${bullets.length} records, one per bullet above, in the same order, with \`ordinal\` matching the number shown. Do not merge, split, add or drop bullets.`,
	);

	if (fixNotes != null && fixNotes.trim().length > 0) {
		parts.push(
			"",
			"## Fix these problems from your previous attempt",
			fixNotes.trim(),
			"",
			"Reply with the FULL corrected list again (all bullets, not only the ones listed).",
		);
	}

	return parts.join("\n");
}

/** The JSON shape `CallLLM` validates each attempt against.
 *
 * Deliberately loose: it only pins the OUTER shape (`bullets`, each with an integer `ordinal` and a
 * non-empty `selfContainedText`). Enum membership, clamping, completeness and the nullability of
 * `filterReason` are enforced in `Index.ts`, which lets the retry message explain a problem in plain
 * language ("bullet 4's eventType is not one of ...") instead of emitting a schema error. */
export function Index_JSONSchema(): JSONSchemaLike {
	return {
		type: "object",
		required: ["bullets"],
		properties: {
			bullets: {
				type: "array",
				minItems: 1,
				items: {
					type: "object",
					required: ["ordinal", "selfContainedText"],
					properties: {
						ordinal: {type: "integer"},
						selfContainedText: {type: "string", minLength: 1},
					},
				},
			},
		},
	};
}
